/**
 * Phase 3H Part B — Settings → Agreements & Terms panel.
 *
 * Owner-only. Reads GET /api/hq/control/agreements/templates and
 * lists all territory × service_type × version rows so an owner can
 * see status/legal-review-required and (via the existing owner
 * `/activate` endpoint) promote a DRAFT to ACTIVE.
 * The panel NEVER exposes the raw template body — that is legal
 * content, edited only through the deploy pipeline.
 */
import React, { useEffect, useState } from "react";
import { hqApi } from "@/hq/hqApi";
import { Card, EmptyState, Skeleton } from "@/hq/HQPrimitives";
import { toast } from "sonner";
import { ShieldCheck, CheckCircle2, AlertTriangle, XCircle, Eye } from "lucide-react";

const STATUS_TONE = {
  DRAFT:   "bg-[#FFF7EE] text-[#8A4A00] border-[#FCE1C7]",
  ACTIVE:  "bg-[#E7F7EE] text-[#0FA958] border-[#B7E4C7]",
  RETIRED: "bg-[#F1F1F1] text-[#8A8A8A] border-[#EAEAEA]",
};

const TERRITORY_LABEL = {
  UK: "United Kingdom", UAE: "United Arab Emirates",
  US: "United States",  EU_EEA: "European Union / EEA",
};

const fmt = (iso) => iso ? new Date(iso).toLocaleDateString(undefined, { year:"numeric", month:"short", day:"numeric" }) : "—";

export default function SettingsAgreementsPanel() {
  const [rows, setRows] = useState(null);
  const [readiness, setReadiness] = useState(null);
  const [preview, setPreview] = useState(null);
  const [err, setErr]   = useState("");
  const [busy, setBusy] = useState("");

  const load = () => {
    hqApi.get("/control/agreements/templates")
      .then((r) => setRows(r.data.items || []))
      .catch((e) => setErr(e?.response?.data?.detail || "Failed to load templates"));
    hqApi.get("/control/agreements/legal-readiness")
      .then((r) => setReadiness(r.data.territories || []))
      .catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const previewTpl = async (tpl) => {
    try {
      const r = await hqApi.get(`/control/agreements/templates/${tpl.id}`);
      setPreview(r.data);
    } catch (e) {
      toast.error("Failed to load preview");
    }
  };

  const [approveModal, setApproveModal] = useState(null); // single template
  const [bulkModal, setBulkModal] = useState(false);
  const [bulkConfirmed, setBulkConfirmed] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);

  const openApprove = (tpl) => { setApproveModal(tpl); };
  const doApprove = async () => {
    if (!approveModal) return;
    setBusy(approveModal.id);
    try {
      const r = await hqApi.post(`/control/agreements/templates/${approveModal.id}/approve-and-activate`);
      if (r.data?.result === "activated") toast.success(`Approved & activated ${approveModal.service_type} · ${approveModal.territory}`);
      else if (r.data?.result === "already_active") toast(`Already active`);
      setApproveModal(null);
      load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "object" ? (d.reason || "Failed to activate") : (d || "Failed to activate"));
    } finally { setBusy(""); }
  };

  const doBulkApprove = async () => {
    setBusy("bulk");
    try {
      const r = await hqApi.post("/control/agreements/templates/approve-and-activate-all");
      setBulkResult(r.data);
      toast.success(`Bulk approve: ${r.data?.counts?.activated || 0} activated, ${r.data?.counts?.blocked || 0} blocked`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk approval failed");
    } finally { setBusy(""); }
  };

  const activate = async (tpl) => {
    // Phase 3I.4E — clicking ACTIVATE on a template with lrr=true opens
    // the owner-approval modal; otherwise runs the legacy activate path.
    if (tpl.legal_review_required) return openApprove(tpl);
    if (!window.confirm(`Activate ${tpl.agreement_type} · ${tpl.service_type} · ${tpl.territory} v${tpl.version}?\nThis will RETIRE the currently ACTIVE template for the same combo.`)) return;
    setBusy(tpl.id);
    try {
      await hqApi.post(`/control/agreements/templates/${tpl.id}/activate`);
      toast.success(`Activated ${tpl.agreement_reference || tpl.id.slice(0,8)}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to activate");
    } finally { setBusy(""); }
  };

  // Phase 3I.4G — DEACTIVATE ACTIVE template.
  const [deactivateModal, setDeactivateModal] = useState(null);
  const doDeactivate = async () => {
    if (!deactivateModal) return;
    setBusy(deactivateModal.id);
    try {
      await hqApi.post(
        `/control/agreements/templates/${deactivateModal.id}/deactivate`,
        { mark_review_required: false }
      );
      toast.success(`Deactivated ${deactivateModal.service_type} · ${deactivateModal.territory}`);
      setDeactivateModal(null);
      load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "object" ? (d.message || d.code || "Failed to deactivate") : (d || "Failed to deactivate"));
    } finally { setBusy(""); }
  };

  if (err) return <Card className="p-6"><div className="text-sm text-[#C1350B]">{err}</div></Card>;
  if (rows === null) return <div className="space-y-3"><Skeleton className="h-24"/><Skeleton className="h-24"/></div>;
  if (rows.length === 0) return <EmptyState title="No templates seeded" description="Run the seed_templates script to populate the initial template library."/>;

  // Group by (agreement_type, service_type, territory)
  const groups = {};
  rows.forEach((r) => {
    const k = `${r.agreement_type}|${r.service_type}|${r.territory}`;
    (groups[k] = groups[k] || []).push(r);
  });

  return (
    <div className="space-y-4" data-testid="settings-agreements-panel">
      <Card className="p-5">
        <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-[#FF6A00]">Legal · Immutable</div>
        <div className="mt-1 flex items-start justify-between gap-4">
          <h2 className="font-black text-lg">Agreements &amp; Terms</h2>
          {rows.some((r) => r.status === "DRAFT" && r.legal_review_required) && (
            <button
              data-testid="settings-tpl-bulk-approve"
              onClick={() => { setBulkModal(true); setBulkConfirmed(false); setBulkResult(null); }}
              disabled={busy === "bulk"}
              className="shrink-0 px-4 py-2 rounded-full font-black text-[11px] tracking-[0.16em] uppercase bg-[#FF6A00] text-black hover:bg-[#e75f00] disabled:opacity-60"
            >
              Approve &amp; Activate All
            </button>
          )}
        </div>
        <p className="mt-2 text-sm text-[#4A4A4A]">
          Manage territory-specific commercial agreement templates. Editing an ACTIVE template creates a
          new VERSION — historical accepted agreement snapshots stay immutable and always render exactly
          the wording the customer accepted.
        </p>
      </Card>

      {/* Legal Readiness — informational only */}
      {readiness && (
        <Card className="p-0 overflow-hidden" data-testid="legal-readiness-panel">
          <div className="p-5 border-b border-[#EAEAEA]">
            <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-[#FF6A00]">Legal Readiness</div>
            <h3 className="mt-1 font-black text-base">Territories × service coverage</h3>
            <p className="mt-1 text-[11px] text-[#8A8A8A]">
              Informational only. Nothing on this page is legally approved unless an owner explicitly activates
              a template after external legal review.
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-[12px]">
              <thead className="bg-[#FDFCF9] text-[9px] tracking-[0.18em] uppercase font-bold text-[#8A8A8A]">
                <tr>
                  <th className="text-left px-4 py-3">Territory</th>
                  <th className="text-left px-3 py-3">Commercial copy</th>
                  <th className="text-left px-3 py-3">Governing law</th>
                  <th className="text-left px-3 py-3">Liability</th>
                  <th className="text-left px-3 py-3">Privacy / DPA</th>
                  <th className="text-left px-3 py-3">Mandatory overrides</th>
                  <th className="text-left px-3 py-3">Overall</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#EAEAEA]">
                {readiness.map((t) => (
                  <tr key={t.territory} data-testid={`legal-readiness-row-${t.territory}`}>
                    <td className="px-4 py-3 font-black">{t.territory.replace("_", " / ")}</td>
                    <ReadinessCell status={worstOf(t.cells, "service")}/>
                    <ReadinessCell status={t.overall === "READY" ? "READY" : "REVIEW_REQUIRED"}/>
                    <ReadinessCell status={t.overall === "READY" ? "READY" : "REVIEW_REQUIRED"}/>
                    <ReadinessCell status={t.overall === "READY" ? "READY" : "REVIEW_REQUIRED"}/>
                    <ReadinessCell status={t.overall === "READY" ? "READY" : "REVIEW_REQUIRED"}/>
                    <ReadinessCell status={t.overall} testId={`legal-readiness-overall-${t.territory}`}/>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {Object.entries(groups).map(([k, list]) => {
        const [at, st, ter] = k.split("|");
        const sorted = [...list].sort((a, b) => (b.version_int || 0) - (a.version_int || 0));
        return (
          <Card key={k} className="p-5" data-testid={`settings-agreement-group-${k.replace(/\|/g,'-')}`}>
            <div className="flex items-baseline gap-3 flex-wrap">
              <div>
                <div className="text-[10px] tracking-[0.24em] uppercase font-bold text-[#8A8A8A]">{at}</div>
                <div className="mt-0.5 font-black text-base capitalize">
                  {st.replace(/_/g, " ")} · {TERRITORY_LABEL[ter] || ter}
                </div>
              </div>
            </div>
            <div className="mt-3 divide-y divide-[#EAEAEA]">
              {sorted.map((tpl) => (
                <div key={tpl.id} className="py-3 flex flex-col md:flex-row md:items-center gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="font-black text-sm">v{tpl.version}</div>
                      {tpl.status === "ACTIVE" && tpl.legal_review_required ? (
                        <span className="text-[9px] tracking-[0.14em] uppercase font-black px-2 py-0.5 rounded-full bg-[#FFEDE5] text-[#C1350B] border border-[#F5C1B0]"
                              data-testid={`settings-tpl-status-${tpl.id}`}>
                          Active — Blocked
                        </span>
                      ) : (
                        <span className={`text-[9px] tracking-[0.14em] uppercase font-black px-2 py-0.5 rounded-full border ${STATUS_TONE[tpl.status] || STATUS_TONE.DRAFT}`}
                              data-testid={`settings-tpl-status-${tpl.id}`}>
                          {tpl.status}
                        </span>
                      )}
                      {tpl.legal_review_required && (
                        <span className="text-[9px] tracking-[0.14em] uppercase font-black px-2 py-0.5 rounded-full bg-[#FFF9E6] text-[#8A6D00] border border-[#F5E4B0]">
                          <ShieldCheck size={9} className="inline -mt-0.5 mr-0.5"/> Legal review required
                        </span>
                      )}
                      {tpl.status === "ACTIVE" && !tpl.legal_review_required && (
                        <span className="text-[9px] tracking-[0.14em] uppercase font-black px-2 py-0.5 rounded-full bg-[#E7F7EE] text-[#0FA958] border border-[#B7E4C7]">
                          <CheckCircle2 size={9} className="inline -mt-0.5 mr-0.5"/> Legal approved
                        </span>
                      )}
                    </div>
                    <div className="mt-1 text-[11px] text-[#8A8A8A]">
                      Created {fmt(tpl.created_at)} · Activated {fmt(tpl.activated_at)} {tpl.retired_at ? `· Retired ${fmt(tpl.retired_at)}` : ""}
                    </div>
                  </div>
                  {tpl.status === "DRAFT" && (
                    <button
                      data-testid={`settings-tpl-activate-${tpl.id}`}
                      onClick={() => activate(tpl)}
                      disabled={busy === tpl.id}
                      className="px-3 py-1.5 rounded-full text-[10px] font-black tracking-[0.14em] uppercase bg-[#FF6A00] text-black hover:bg-[#e75f00] disabled:opacity-50"
                    >
                      {busy === tpl.id ? "Activating…" : "Activate"}
                    </button>
                  )}
                  {tpl.status === "ACTIVE" && (
                    <button
                      data-testid={`settings-tpl-deactivate-${tpl.id}`}
                      onClick={() => setDeactivateModal(tpl)}
                      disabled={busy === tpl.id}
                      className="px-3 py-1.5 rounded-full text-[10px] font-black tracking-[0.14em] uppercase bg-white border border-[#EAEAEA] text-[#C1350B] hover:border-[#C1350B] disabled:opacity-50"
                    >
                      Deactivate
                    </button>
                  )}
                  <button
                    data-testid={`settings-tpl-preview-${tpl.id}`}
                    onClick={() => previewTpl(tpl)}
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-[10px] font-black tracking-[0.14em] uppercase bg-white border border-[#EAEAEA] hover:border-[#FF6A00]"
                  >
                    <Eye size={11}/> Preview
                  </button>
                </div>
              ))}
            </div>
          </Card>
        );
      })}

      {/* Phase 3I.4G — Deactivate confirmation modal */}
      {deactivateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" data-testid="settings-tpl-deactivate-modal">
          <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl overflow-hidden">
            <div className="p-5 border-b border-[#EAEAEA]">
              <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-[#C1350B]">Owner Action</div>
              <div className="mt-1 font-black text-lg">Deactivate Agreement Template</div>
            </div>
            <div className="p-5 text-sm text-[#4A4A4A] leading-relaxed">
              <p>This will stop this agreement template from being used for new customer agreements.</p>
              <p className="mt-2">Existing agreements that have already been issued or accepted will remain unchanged.</p>
              <dl className="mt-4 grid grid-cols-2 gap-x-4 gap-y-1 text-[12px]">
                <dt className="text-[#8A8A8A]">Service</dt><dd className="font-bold">{deactivateModal.service_type}</dd>
                <dt className="text-[#8A8A8A]">Territory</dt><dd className="font-bold">{TERRITORY_LABEL[deactivateModal.territory] || deactivateModal.territory}</dd>
                <dt className="text-[#8A8A8A]">Version</dt><dd className="font-bold">v{deactivateModal.version}</dd>
                <dt className="text-[#8A8A8A]">Current status</dt><dd className="font-bold">{deactivateModal.status}</dd>
              </dl>
            </div>
            <div className="p-4 border-t border-[#EAEAEA] flex justify-end gap-2">
              <button data-testid="settings-tpl-deactivate-cancel" onClick={() => setDeactivateModal(null)} className="px-4 py-2 rounded-full text-sm font-bold text-[#4A4A4A] hover:bg-[#F6F5F2]">Cancel</button>
              <button data-testid="settings-tpl-deactivate-confirm" onClick={doDeactivate} disabled={busy === deactivateModal.id} className="px-4 py-2 rounded-full font-black text-[11px] tracking-[0.16em] uppercase bg-[#C1350B] text-white hover:bg-[#a02b09] disabled:opacity-60">
                {busy === deactivateModal.id ? "Working..." : "Deactivate Template"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Preview modal */}
      {preview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" data-testid="settings-tpl-preview-modal">
          <div className="bg-white rounded-2xl w-full max-w-3xl shadow-2xl overflow-hidden max-h-[90vh] flex flex-col">
            <div className="p-4 border-b border-[#EAEAEA] flex items-center justify-between">
              <div className="min-w-0">
                <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-[#FF6A00]">Template preview · DRAFT · LEGAL REVIEW REQUIRED</div>
                <div className="mt-0.5 font-black text-base truncate">{preview.title}</div>
              </div>
              <button onClick={() => setPreview(null)} className="p-2 rounded-full hover:bg-[#F6F5F2]" data-testid="settings-tpl-preview-close">✕</button>
            </div>
            <div className="p-5 overflow-y-auto whitespace-pre-wrap text-[12px] leading-relaxed font-mono bg-[#FDFCF9] flex-1">
              {preview.template_content}
            </div>
          </div>
        </div>
      )}

      {/* Phase 3I.4E — Single-template approve-and-activate modal */}
      {approveModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" data-testid="settings-tpl-approve-modal">
          <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl overflow-hidden">
            <div className="p-5 border-b border-[#EAEAEA]">
              <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-[#FF6A00]">Approve &amp; Activate Agreement</div>
              <div className="mt-1 font-black text-lg">Approve &amp; Activate Agreement</div>
            </div>
            <div className="p-5 text-sm text-[#4A4A4A] leading-relaxed">
              <p>This agreement is currently marked as requiring legal review. Approving it will mark this version as legally approved within Orange HQ and make it eligible for customer use.</p>
              <p className="mt-3">Only continue if you are authorised to approve this agreement version.</p>
              <dl className="mt-4 grid grid-cols-2 gap-x-4 gap-y-1 text-[12px]">
                <dt className="text-[#8A8A8A]">Service</dt><dd className="font-bold">{approveModal.service_type}</dd>
                <dt className="text-[#8A8A8A]">Territory</dt><dd className="font-bold">{TERRITORY_LABEL[approveModal.territory] || approveModal.territory}</dd>
                <dt className="text-[#8A8A8A]">Version</dt><dd className="font-bold">v{approveModal.version}</dd>
                <dt className="text-[#8A8A8A]">Current status</dt><dd className="font-bold">{approveModal.status}</dd>
                <dt className="text-[#8A8A8A]">Legal Review Required</dt><dd className="font-bold text-[#C1350B]">Yes</dd>
              </dl>
            </div>
            <div className="p-4 border-t border-[#EAEAEA] flex justify-end gap-2">
              <button data-testid="settings-tpl-approve-cancel" onClick={() => setApproveModal(null)} className="px-4 py-2 rounded-full text-sm font-bold text-[#4A4A4A] hover:bg-[#F6F5F2]">Cancel</button>
              <button data-testid="settings-tpl-approve-confirm" onClick={doApprove} disabled={busy === approveModal.id} className="px-4 py-2 rounded-full font-black text-[11px] tracking-[0.16em] uppercase bg-[#FF6A00] text-black hover:bg-[#e75f00] disabled:opacity-60">
                {busy === approveModal.id ? "Working..." : "Approve & Activate"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Phase 3I.4E — Bulk approve-and-activate modal */}
      {bulkModal && (() => {
        const drafts = rows.filter((r) => r.status === "DRAFT" && r.legal_review_required);
        const active = rows.filter((r) => r.status === "ACTIVE").length;
        return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" data-testid="settings-tpl-bulk-modal">
          <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl overflow-hidden">
            <div className="p-5 border-b border-[#EAEAEA]">
              <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-[#FF6A00]">Owner Action</div>
              <div className="mt-1 font-black text-lg">Approve &amp; Activate All Agreement Templates</div>
            </div>
            <div className="p-5 text-sm text-[#4A4A4A] leading-relaxed">
              <p>You are about to approve and activate all currently eligible OrangeBySite agreement templates. This will clear the Legal Review Required flag and make approved templates available for customer agreements.</p>
              <p className="mt-3">Templates that fail readiness checks will NOT be activated.</p>
              <dl className="mt-4 grid grid-cols-2 gap-x-4 gap-y-1 text-[12px]">
                <dt className="text-[#8A8A8A]">Total templates</dt><dd className="font-bold">{rows.length}</dd>
                <dt className="text-[#8A8A8A]">Eligible (DRAFT · LRR)</dt><dd className="font-bold">{drafts.length}</dd>
                <dt className="text-[#8A8A8A]">Already active</dt><dd className="font-bold">{active}</dd>
              </dl>
              <label className="mt-5 flex items-start gap-2 text-[12px]">
                <input data-testid="settings-tpl-bulk-checkbox" type="checkbox" checked={bulkConfirmed} onChange={(e) => setBulkConfirmed(e.target.checked)} className="mt-0.5"/>
                <span>I confirm I am authorised to approve these agreement templates.</span>
              </label>
              {bulkResult && (
                <div data-testid="settings-tpl-bulk-result" className="mt-4 p-3 rounded-lg border border-[#EAEAEA] bg-[#F6F5F2] text-[12px]">
                  <div className="font-bold">Result</div>
                  <div>Activated: {bulkResult.counts?.activated || 0}</div>
                  <div>Already active: {bulkResult.counts?.already_active || 0}</div>
                  <div>Blocked: {bulkResult.counts?.blocked || 0}</div>
                  {(bulkResult.per_template || []).filter((r) => r.result === "blocked").map((r) => (
                    <div key={r.template_id} className="mt-1 text-[#C1350B]">{r.service_type}/{r.territory}: {r.reason}</div>
                  ))}
                </div>
              )}
            </div>
            <div className="p-4 border-t border-[#EAEAEA] flex justify-end gap-2">
              <button data-testid="settings-tpl-bulk-cancel" onClick={() => setBulkModal(false)} className="px-4 py-2 rounded-full text-sm font-bold text-[#4A4A4A] hover:bg-[#F6F5F2]">Cancel</button>
              <button data-testid="settings-tpl-bulk-confirm" onClick={doBulkApprove} disabled={!bulkConfirmed || busy === "bulk"} className="px-4 py-2 rounded-full font-black text-[11px] tracking-[0.16em] uppercase bg-[#FF6A00] text-black hover:bg-[#e75f00] disabled:opacity-60">
                {busy === "bulk" ? "Working..." : `Approve & Activate All ${drafts.length}`}
              </button>
            </div>
          </div>
        </div>
        );
      })()}
    </div>
  );
}

function ReadinessCell({ status, testId }) {
  const cfg = {
    READY:            { cls: "text-[#0FA958] bg-[#E7F7EE] border-[#B7E4C7]", Icon: CheckCircle2, label: "Ready" },
    REVIEW_REQUIRED:  { cls: "text-[#8A6D00] bg-[#FFF9E6] border-[#F5E4B0]", Icon: AlertTriangle, label: "Review" },
    NOT_CONFIGURED:   { cls: "text-[#C1350B] bg-[#FDEAEA] border-[#F5CAC3]", Icon: XCircle,      label: "Not set" },
  }[status] || { cls: "text-[#8A8A8A] bg-[#F6F5F2] border-[#EAEAEA]", Icon: AlertTriangle, label: "—" };
  const { Icon } = cfg;
  return (
    <td className="px-3 py-3" data-testid={testId}>
      <span className={`inline-flex items-center gap-1 text-[9px] tracking-[0.14em] uppercase font-black px-2 py-0.5 rounded-full border ${cfg.cls}`}>
        <Icon size={10}/> {cfg.label}
      </span>
    </td>
  );
}

function worstOf(cells, key) {
  if (cells.some((c) => c.status === "NOT_CONFIGURED")) return "NOT_CONFIGURED";
  if (cells.some((c) => c.status === "REVIEW_REQUIRED")) return "REVIEW_REQUIRED";
  return "READY";
}
